import Link from '@tiptap/extension-link';
import { TaskItem, TaskList } from '@tiptap/extension-list';
import Mention from '@tiptap/extension-mention';
import Paragraph from '@tiptap/extension-paragraph';
import Typography from '@tiptap/extension-typography';
import StarterKit from '@tiptap/starter-kit';
import ImageResize from 'tiptap-extension-resize-image';
import { imageExtend } from '../image/extension/extend';
import { getRenderMentionPopup } from '../mentions';
import { createPlaceholderExtension } from './placeholder';
import { isSuggestionQueryDismissed } from './suggestion-allow';
const CLASSES = {
    richTaskItem: 'rich-task-item',
    richTaskList: 'rich-task-list',
    mention: 'mention',
};
const DATA_ATTRIBUTE_PREFIX = 'data';
const DATA_ATTRIBUTE_ID = 'id';
const DATA_ATTRIBUTE_ENTITY = 'entity';
const DATA_ATTRIBUTE_TYPE = 'type';
export function buildEditorExtensions({ mode = 'editor', onError, uploadFile, linkOptions, placeholderOptions, mentionsPopupParamsRef, mentionClassName, }) {
    const list = [
        StarterKit.configure({
            // Disable link and paragraph to configure them manually
            link: false,
            paragraph: false,
        }),
        Paragraph.configure({ HTMLAttributes: { style: 'margin:0;min-height:1em' } }),
        TaskList.configure({ HTMLAttributes: { class: CLASSES.richTaskList } }),
        TaskItem.configure({ nested: true, HTMLAttributes: { class: CLASSES.richTaskItem } }),
        Typography,
        Link.extend({
            addKeyboardShortcuts() {
                return {};
            },
        }).configure({
            protocols: ['ftp', 'mailto'],
            HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
            autolink: true,
            ...linkOptions,
        }),
    ];
    const imageExtension = ImageResize.extend(imageExtend({ onError, uploadFile, enableInteractions: mode === 'editor' }));
    list.push(imageExtension);
    const placeholderExtension = createPlaceholderExtension({
        mode,
        placeholderOptions,
    });
    if (placeholderExtension) {
        list.push(placeholderExtension);
    }
    const mentionClass = mentionClassName || CLASSES.mention;
    const idAttribute = getDataAttribute(DATA_ATTRIBUTE_ID);
    const entityAttribute = getDataAttribute(DATA_ATTRIBUTE_ENTITY);
    const typeAttribute = getDataAttribute(DATA_ATTRIBUTE_TYPE);
    const CustomMention = Mention.extend({
        addAttributes() {
            return { id: { default: null }, label: { default: null }, entity: { default: null } };
        },
        parseHTML() {
            const parseMentionEl = (el) => {
                const text = el.textContent ?? '';
                return {
                    id: el.getAttribute(idAttribute),
                    entity: el.getAttribute(entityAttribute),
                    label: text.startsWith('@') ? text.slice(1) : text,
                };
            };
            return [
                {
                    tag: `span[${typeAttribute}="mention"]`,
                    getAttrs: (el) => (el instanceof HTMLElement ? parseMentionEl(el) : false),
                },
                {
                    tag: `span.${mentionClass}[${idAttribute}]`,
                    getAttrs: (el) => (el instanceof HTMLElement ? parseMentionEl(el) : false),
                },
            ];
        },
    });
    list.push(CustomMention.configure({
        HTMLAttributes: { class: mentionClass },
        suggestion: mode === 'editor'
            ? {
                render: getRenderMentionPopup(mentionsPopupParamsRef),
                allowSpaces: true,
                allow: ({ state, range }) => !isSuggestionQueryDismissed(state, range, '@'),
            }
            : undefined,
        renderHTML({ node }) {
            return [
                'span',
                {
                    [idAttribute]: node.attrs.id,
                    [entityAttribute]: node.attrs.entity,
                    [typeAttribute]: 'mention',
                    class: mentionClass,
                },
                `@${node.attrs.label}`,
            ];
        },
    }));
    return list;
}
export function getDataAttribute(name) {
    return [DATA_ATTRIBUTE_PREFIX, name].join('-');
}
export { Highlight, HighlightCssClass } from './highlight';
export * from './slash-command';
